/** @jsxImportSource preact */
import { h } from 'preact';
import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import * as echarts from 'echarts';
import { FilterControls, StackByOption } from './FilterControls';
import { ViewModeToggle, ViewMode } from './ViewModeToggle';
import { DocumentListModal, DocumentListItem, ActiveFilter } from './DocumentListModal';
import { DocumentDetailModal, DocumentDetail } from './DocumentDetailModal';
import { getCoverageColor, getCategoryColor, getChartTheme, detectDarkMode, getResponsiveConfig, loadTheme } from '@/lib/theme';
import { getSubdomainName, getSubdomainDescription, getSubdomainInfo } from '@/data/riskTaxonomy';
import { SECTOR_FIELDS, ACTOR_ROLES, DEFAULT_ACTOR_ROLE } from '@/data/chartConfigs';
import type { ChartConfig, ActorRole } from '@/data/chartConfigs';

export interface CategoryBarChartProps {
  config: ChartConfig;
  apiBase: string;
  containerId: string;
  initialFilters?: Record<string, string>;
  initialStackBy?: StackByOption;
  initialViewMode?: ViewMode;
}

// Global registry for external filter control
export interface ChartController {
  setStackBy: (value: StackByOption) => void;
  setCoverageFilter: (value: string | string[]) => void;
  setJurisdictionFilter: (value: string | string[]) => void;
  setAuthorityFilter: (value: string | string[]) => void;
  setLegislativeStatusFilter: (value: string | string[]) => void;
  setActivityFilter: (value: string | string[]) => void;
  setAppliesToFilter: (value: string | string[]) => void;
  setLifecycleStageFilter: (value: string | string[]) => void;
  setTechnicalScopeFilter: (value: string | string[]) => void;
  setRiskSubdomainFilter: (value: string | string[]) => void;
  setSectorFilter: (value: string | string[]) => void;
  setViewMode: (value: ViewMode) => void;
  setActorRole: (value: ActorRole) => void;
  resetFilters: () => void;
}

const normalizeFilter = (v: string | string[]): string[] =>
  Array.isArray(v) ? v : v ? [v] : [];

const chartRegistry = new Map<string, ChartController>();

export function getChartController(id: string): ChartController | undefined {
  return chartRegistry.get(id);
}

interface FilterOption {
  value: string;
  label: string;
}

interface DataRow {
  id: string;
  title: string;
  values: number[];
}

interface ApiResponse {
  seriesCategories: string[];
  data: DataRow[];
  totalDocumentsAll: number;
  jurisdictions?: string[];
  authorities?: string[];
  legislativeStatuses?: string[];
  activities?: string[];
  appliesToOptions?: string[];
  lifecycleStageOptions?: string[];
  technicalScopeOptions?: string[];
}

const STACK_BY_LABELS: Record<StackByOption, string> = {
  coverage: 'Level of coverage:',
  jurisdiction: 'Jurisdiction:',
  authority: 'Authority:',
  legislativeStatus: 'Legislative status:',
  activity: 'Most recent activity:',
};

const COVERAGE_BY_SERIES: Record<string, number> = {
  'Good coverage': 3,
  'Minimal coverage': 2,
  'No coverage': 1,
};

const ROW_HEIGHT = 25.5;
const MOBILE_BREAKPOINT = 640;

const COVERAGE_LEGEND_TOOLTIPS: Record<string, string> = {
  'Good coverage': 'Good Coverage: Comprehensive governance measures or mitigations with clear procedures relating to the risk subdomain are explicitly described in the document.',
  'Minimal coverage': 'Minimal Coverage: Brief mention but no specific mitigations, recommendations or governance measures relating to the risk subdomain are described.',
  'No coverage': 'No Coverage.',
};

// Details filter param name mapping
const DETAILS_FILTER_PARAMS: Record<string, string> = {
  subdomain: 'subdomain',
  riskDomain: 'riskDomain',
  sector: 'sector',
  lifecycle: 'lifecycleValue',
  technicalScope: 'technicalScopeValue',
  legislativeStatus: 'legislativeStatusValue',
  actors: 'actorValue',
};

// Static filter options for cross-cutting filters
const SUBDOMAIN_IDS = [
  '1.1','1.2','1.3','2.1','2.2','3.1','3.2','4.1','4.2','4.3',
  '5.1','5.2','6.1','6.2','6.3','6.4','6.5','6.6','7.1','7.2','7.3','7.4','7.5','7.6',
];

const RISK_SUBDOMAIN_OPTIONS: FilterOption[] = SUBDOMAIN_IDS.map((id) => ({
  value: id,
  label: `${id} - ${getSubdomainInfo(id)?.name ?? id}`,
}));

const SECTOR_OPTIONS: FilterOption[] = SECTOR_FIELDS.map((sf) => ({
  value: sf.id,
  label: sf.label,
}));

function niceMax(value: number): number {
  if (value <= 0) return 1;
  // Compute a nice tick interval targeting ~5 ticks, then round up
  const rawInterval = value / 5;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawInterval)));
  const normalized = rawInterval / magnitude;
  let tickInterval: number;
  if (normalized <= 1.5) tickInterval = magnitude;
  else if (normalized <= 3.5) tickInterval = 2 * magnitude;
  else if (normalized <= 7.5) tickInterval = 5 * magnitude;
  else tickInterval = 10 * magnitude;
  return Math.ceil(value / tickInterval) * tickInterval;
}

export function CategoryBarChart({
  config,
  apiBase,
  containerId,
  initialFilters = {},
  initialStackBy,
  initialViewMode,
}: CategoryBarChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const theme = loadTheme();

  const defaultStackBy = initialStackBy || config.defaultStackBy;
  const defaultViewMode = initialViewMode || (config.defaultViewMode as ViewMode);

  // State
  const [stackBy, setStackBy] = useState<StackByOption>(defaultStackBy);
  const [viewMode, setViewMode] = useState<ViewMode>(defaultViewMode);
  const [coverageFilter, setCoverageFilter] = useState<string[]>(
    initialFilters.coverage ? [initialFilters.coverage] : []
  );
  const [jurisdictionFilter, setJurisdictionFilter] = useState<string[]>(
    initialFilters.jurisdiction ? [initialFilters.jurisdiction] : []
  );
  const [authorityFilter, setAuthorityFilter] = useState<string[]>(
    initialFilters.authority ? [initialFilters.authority] : []
  );
  const [legislativeStatusFilter, setLegislativeStatusFilter] = useState<string[]>(
    initialFilters.legislativeStatus ? [initialFilters.legislativeStatus] : []
  );
  const [activityFilter, setActivityFilter] = useState<string[]>(
    initialFilters.activity ? [initialFilters.activity] : []
  );
  const [appliesToFilter, setAppliesToFilter] = useState<string[]>(
    initialFilters.appliesTo ? [initialFilters.appliesTo] : []
  );
  const [lifecycleStageFilter, setLifecycleStageFilter] = useState<string[]>(
    initialFilters.lifecycleStage ? [initialFilters.lifecycleStage] : []
  );
  const [technicalScopeFilter, setTechnicalScopeFilter] = useState<string[]>(
    initialFilters.technicalScope ? [initialFilters.technicalScope] : []
  );
  const [riskSubdomainFilter, setRiskSubdomainFilter] = useState<string[]>(
    initialFilters.riskSubdomain ? [initialFilters.riskSubdomain] : []
  );
  const [sectorFilter, setSectorFilter] = useState<string[]>(
    initialFilters.sector ? [initialFilters.sector] : []
  );
  const [actorRole, setActorRole] = useState<ActorRole>(
    (initialFilters.actorRole as ActorRole) || DEFAULT_ACTOR_ROLE
  );

  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ApiResponse | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(detectDarkMode());
  const [isMobile, setIsMobile] = useState(false);

  // Filter options from API
  const [jurisdictions, setJurisdictions] = useState<FilterOption[]>([]);
  const [authorities, setAuthorities] = useState<FilterOption[]>([]);
  const [legislativeStatuses, setLegislativeStatuses] = useState<FilterOption[]>([]);
  const [activities, setActivities] = useState<FilterOption[]>([]);
  const [appliesToOptions, setAppliesToOptions] = useState<FilterOption[]>([]);
  const [lifecycleStageOptions, setLifecycleStageOptions] = useState<FilterOption[]>([]);
  const [technicalScopeOptions, setTechnicalScopeOptions] = useState<FilterOption[]>([]);

  // Document list modal state
  const [listModalOpen, setListModalOpen] = useState(false);
  const [listModalTitle, setListModalTitle] = useState('');
  const [listModalItems, setListModalItems] = useState<DocumentListItem[]>([]);
  const [listModalLoading, setListModalLoading] = useState(false);
  const [listModalFilters, setListModalFilters] = useState<ActiveFilter[]>([]);

  // Document detail modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailDocumentId, setDetailDocumentId] = useState('');
  const [detailDocument, setDetailDocument] = useState<DocumentDetail | null>(null);
  const [detailModalLoading, setDetailModalLoading] = useState(false);

  // Register controller for external access
  useEffect(() => {
    const controller: ChartController = {
      setStackBy,
      setCoverageFilter: (v) => setCoverageFilter(normalizeFilter(v)),
      setJurisdictionFilter: (v) => setJurisdictionFilter(normalizeFilter(v)),
      setAuthorityFilter: (v) => setAuthorityFilter(normalizeFilter(v)),
      setLegislativeStatusFilter: (v) => setLegislativeStatusFilter(normalizeFilter(v)),
      setActivityFilter: (v) => setActivityFilter(normalizeFilter(v)),
      setAppliesToFilter: (v) => setAppliesToFilter(normalizeFilter(v)),
      setLifecycleStageFilter: (v) => setLifecycleStageFilter(normalizeFilter(v)),
      setTechnicalScopeFilter: (v) => setTechnicalScopeFilter(normalizeFilter(v)),
      setRiskSubdomainFilter: (v) => setRiskSubdomainFilter(normalizeFilter(v)),
      setSectorFilter: (v) => setSectorFilter(normalizeFilter(v)),
      setViewMode,
      setActorRole,
      resetFilters: () => {
        setStackBy(config.defaultStackBy);
        setCoverageFilter([]);
        setJurisdictionFilter([]);
        setAuthorityFilter([]);
        setLegislativeStatusFilter([]);
        setActivityFilter([]);
        setAppliesToFilter([]);
        setLifecycleStageFilter([]);
        setTechnicalScopeFilter([]);
        setRiskSubdomainFilter([]);
        setSectorFilter([]);
        setActorRole(DEFAULT_ACTOR_ROLE);
      },
    };
    chartRegistry.set(containerId, controller);
    return () => { chartRegistry.delete(containerId); };
  }, [containerId, config.defaultStackBy]);

  // Detect mobile and dark mode
  useEffect(() => {
    const updateMobile = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    updateMobile();
    window.addEventListener('resize', updateMobile);

    const darkModeMedia = window.matchMedia('(prefers-color-scheme: dark)');
    const updateDarkMode = () => setIsDarkMode(darkModeMedia.matches);
    if (typeof darkModeMedia.addEventListener === 'function') {
      darkModeMedia.addEventListener('change', updateDarkMode);
    }

    return () => {
      window.removeEventListener('resize', updateMobile);
      if (typeof darkModeMedia.removeEventListener === 'function') {
        darkModeMedia.removeEventListener('change', updateDarkMode);
      }
    };
  }, []);

  // Fetch chart data
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const params = new URLSearchParams();
    params.set('stackBy', stackBy);
    if (config.aggregation !== 'risk-subdomain') {
      params.set('aggregation', config.aggregation);
    }
    if (config.aggregation === 'actors') {
      params.set('actorRole', actorRole);
    }
    if (coverageFilter.length) params.set('coverage', coverageFilter.join(','));
    if (jurisdictionFilter.length) params.set('jurisdiction', jurisdictionFilter.join(','));
    if (authorityFilter.length) params.set('authority', authorityFilter.join(','));
    if (legislativeStatusFilter.length) params.set('legislativeStatus', legislativeStatusFilter.join(','));
    if (activityFilter.length) params.set('activity', activityFilter.join(','));
    if (appliesToFilter.length) params.set('appliesTo', appliesToFilter.join(','));
    if (lifecycleStageFilter.length) params.set('lifecycleStage', lifecycleStageFilter.join(','));
    if (technicalScopeFilter.length) params.set('technicalScope', technicalScopeFilter.join(','));
    if (riskSubdomainFilter.length) params.set('riskSubdomain', riskSubdomainFilter.join(','));
    if (sectorFilter.length) params.set('sector', sectorFilter.join(','));

    fetch(`${apiBase}/data/risk-subdomain?${params}`)
      .then((r) => r.json())
      .then((json: ApiResponse) => {
        if (cancelled) return;
        setData(json);

        // Update filter options
        if (json.jurisdictions) {
          setJurisdictions(json.jurisdictions.map((v) => ({ value: v, label: v })));
        }
        if (json.authorities) {
          setAuthorities(json.authorities.map((v) => ({ value: v, label: v })));
        }
        if (json.legislativeStatuses) {
          setLegislativeStatuses(json.legislativeStatuses.map((v) => ({ value: v, label: v })));
        }
        if (json.activities) {
          setActivities(json.activities.map((v) => ({ value: v, label: v })));
        }
        if (json.appliesToOptions) {
          setAppliesToOptions(json.appliesToOptions.map((v: string) => ({ value: v, label: v })));
        }
        if (json.lifecycleStageOptions) {
          setLifecycleStageOptions(json.lifecycleStageOptions.map((v: string) => ({ value: v, label: v })));
        }
        if (json.technicalScopeOptions) {
          setTechnicalScopeOptions(json.technicalScopeOptions.map((v: string) => ({ value: v, label: v })));
        }
      })
      .catch((err) => {
        console.error('Failed to fetch chart data:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, config.aggregation, stackBy, coverageFilter.join(','), jurisdictionFilter.join(','), authorityFilter.join(','), legislativeStatusFilter.join(','), activityFilter.join(','), appliesToFilter.join(','), lifecycleStageFilter.join(','), technicalScopeFilter.join(','), riskSubdomainFilter.join(','), sectorFilter.join(','), actorRole]);

  // Initialize and update chart
  useEffect(() => {
    if (!chartRef.current || !data) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const chart = chartInstance.current;
    const { seriesCategories, data: rows, totalDocumentsAll } = data;

    // For risk subdomains, use taxonomy names; for others, use title from API
    const labels = config.aggregation === 'risk-subdomain'
      ? rows.map((d) => getSubdomainName(d.id) ?? d.title)
      : rows.map((d) => d.title);
    const ids = rows.map((d) => d.id);

    const isProportionView = viewMode === 'proportion';
    const toPercent = (v: number) =>
      totalDocumentsAll > 0 ? (v / totalDocumentsAll) * 100 : 0;

    const chartTheme = getChartTheme(isDarkMode);
    const inheritedColor = getComputedStyle(chartRef.current.parentElement ?? chartRef.current).color || chartTheme.text;
    const containerWidth = chartRef.current.clientWidth;
    const responsiveConfig = getResponsiveConfig(containerWidth);
    const currentIsMobile = containerWidth < MOBILE_BREAKPOINT;

    const {
      labelWidth,
      gridLeft,
      maxLabelLength,
      axisLabelSize = 10,
      axisLabelLineHeight = 12,
      axisLabelMargin = 12,
    } = responsiveConfig;

    const gridBottom = currentIsMobile ? 30 : 30;

    // Build series
    const series = seriesCategories.map((category, index) => ({
      name: category,
      type: 'bar' as const,
      stack: 'total',
      data: rows.map((d) =>
        isProportionView ? toPercent(d.values[index] ?? 0) : d.values[index] ?? 0
      ),
      barCategoryGap: '6.5px',
      itemStyle: {
        color: stackBy === 'coverage'
          ? getCoverageColor(category)
          : getCategoryColor(index),
        borderRadius: index === seriesCategories.length - 1 ? [0, 3, 3, 0] : 0,
      },
    }));

    // Calculate dynamic height
    const chartHeight = Math.max(300, rows.length * ROW_HEIGHT + 180);
    chartRef.current.style.height = `${chartHeight}px`;
    chart.resize();

    // Calculate max value for axis
    const maxStackValue = rows.reduce((max, row) => {
      const total = row.values.reduce(
        (sum, v) => sum + (isProportionView ? toPercent(v) : v),
        0
      );
      return Math.max(max, total);
    }, 0);
    const niceCeil = niceMax(maxStackValue);
    const axisMax = Math.max(1, isProportionView ? Math.min(niceCeil, 100) : niceCeil);

    // Helper functions for label formatting
    const truncateLabel = (label: string, max: number): string => {
      if (label.length <= max) return label;
      return label.slice(0, max - 1) + '…';
    };

    const splitLabel = (label: string, max: number): string => {
      const trimmed = label.trim();
      if (trimmed.length <= max) return trimmed;

      const midpoint = Math.floor(trimmed.length / 2);
      let bestIndex = -1;
      let bestDistance = Infinity;

      for (let i = 0; i < trimmed.length; i++) {
        if (trimmed[i] !== ' ') continue;
        const distance = Math.abs(i - midpoint);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = i;
        }
      }

      if (bestIndex <= 0) return truncateLabel(trimmed, max);

      const firstLine = trimmed.slice(0, bestIndex).trim();
      const secondLine = trimmed.slice(bestIndex + 1).trim();
      return `${firstLine}\n${secondLine}`;
    };

    const formatMobileLabel = (label: string): string => {
      const mobileMaxLength = maxLabelLength;
      const trimmed = label.trim();
      if (trimmed.length <= mobileMaxLength) return trimmed;

      const breakIndex = trimmed.lastIndexOf(' ', mobileMaxLength);
      const firstLine = breakIndex > 0
        ? trimmed.slice(0, breakIndex)
        : trimmed.slice(0, mobileMaxLength);
      const remainder = trimmed
        .slice(breakIndex > 0 ? breakIndex + 1 : mobileMaxLength)
        .trimStart();

      if (!remainder) return firstLine;

      const secondLine = remainder.slice(0, mobileMaxLength);
      const suffix = remainder.length > mobileMaxLength ? '...' : '';
      return `${firstLine}\n${secondLine}${suffix}`;
    };

    const option: echarts.EChartsOption = {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: chartTheme.tooltipBg,
        borderColor: chartTheme.tooltipBorder,
        textStyle: { color: chartTheme.tooltipText },
        formatter: (params: unknown) => {
          const paramsArray = params as Array<{
            dataIndex: number;
            marker: string;
            seriesName: string;
            value: number;
          }>;
          const idx = paramsArray?.[0]?.dataIndex ?? 0;
          const header = config.tooltipHeaderFormat === 'id-label'
            ? `${ids[idx]} - ${labels[idx]}`
            : labels[idx];
          const lines = paramsArray
            .map(
              (p) =>
                `${p.marker}${p.seriesName}: ${
                  isProportionView ? p.value.toFixed(1) + '%' : Math.round(p.value)
                }`
            )
            .join('<br/>');
          return `${header}<br/>${lines}`;
        },
      },
      legend: { show: false, data: seriesCategories },
      grid: {
        left: gridLeft,
        right: 30,
        top: 30,
        bottom: gridBottom,
      },
      graphic: [],
      xAxis: {
        type: 'value',
        min: 0,
        max: axisMax,
        axisLabel: {
          formatter: (value: number) =>
            isProportionView ? `${value}%` : `${Math.round(value)}`,
          color: inheritedColor,
        },
        axisLine: { lineStyle: { color: chartTheme.grid } },
        axisTick: { lineStyle: { color: chartTheme.grid } },
        splitLine: { lineStyle: { color: chartTheme.grid } },
      },
      yAxis: {
        type: 'category',
        data: labels,
        inverse: true,
        triggerEvent: true,
        axisTick: {
          alignWithLabel: true,
          lineStyle: { color: chartTheme.grid },
        },
        axisLine: { lineStyle: { color: chartTheme.grid } },
        axisLabel: {
          width: labelWidth,
          fontSize: axisLabelSize,
          lineHeight: axisLabelLineHeight,
          align: 'right',
          verticalAlign: 'middle',
          margin: axisLabelMargin,
          formatter: (value: string) =>
            currentIsMobile ? formatMobileLabel(value) : splitLabel(value, maxLabelLength),
          color: inheritedColor,
        },
      },
      series,
      animationDuration: theme.chart.animationDuration,
    };

    chart.setOption(option, { notMerge: true });

    // Handle click events
    chart.off('click');
    chart.on('click', (params) => {
      handleChartClick(params, ids, labels);
    });

    // Handle y-axis label hover for description tooltips (risk subdomains only)
    chart.off('mouseover');
    chart.off('mouseout');
    if (config.hasYAxisDescriptions) {
      chart.on('mouseover', (params: unknown) => {
        const p = params as { componentType?: string; value?: string; event?: { event?: MouseEvent } };
        if (p.componentType === 'yAxis' && tooltipRef.current) {
          const row = rows.find((d) => (getSubdomainName(d.id) ?? d.title) === p.value);
          if (row) {
            const description = getSubdomainDescription(row.id);
            if (description) {
              const name = getSubdomainName(row.id) ?? row.title;
              tooltipRef.current.innerHTML = `<strong>${name}</strong><br/>${description}`;
              tooltipRef.current.style.display = 'block';
              const mouseEvent = p.event?.event;
              if (mouseEvent) {
                const el = tooltipRef.current;
                const tooltipWidth = el.offsetWidth;
                const tooltipHeight = el.offsetHeight;
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                let left = mouseEvent.clientX + 15;
                let top = mouseEvent.clientY - 10;
                if (left + tooltipWidth > viewportWidth - 10) {
                  left = mouseEvent.clientX - tooltipWidth - 15;
                }
                if (top + tooltipHeight > viewportHeight - 10) {
                  top = viewportHeight - tooltipHeight - 10;
                }
                if (top < 10) top = 10;
                if (left < 10) left = 10;
                el.style.left = `${left}px`;
                el.style.top = `${top}px`;
              }
            }
          }
        }
      });
      chart.on('mouseout', (params: unknown) => {
        const p = params as { componentType?: string };
        if (p.componentType === 'yAxis' && tooltipRef.current) {
          tooltipRef.current.style.display = 'none';
        }
      });
    }

    return () => {
      chart.off('click');
      chart.off('mouseover');
      chart.off('mouseout');
    };
  }, [data, viewMode, isDarkMode, stackBy, theme, config]);

  // Handle chart click
  const handleChartClick = useCallback((params: echarts.ECElementEvent, ids: string[], labels: string[]) => {
    const { dataIndex, seriesName, componentType } = params;

    if (componentType === 'yAxis') return;

    // Handle bar click - open document list modal
    if (dataIndex !== undefined && seriesName && data) {
      const rowId = ids[dataIndex];
      const rowLabel = labels[dataIndex];
      if (!rowId || !rowLabel) return;

      // Build query params for details API
      const qs = new URLSearchParams();
      qs.set('filterType', config.detailsFilterType);
      const detailsParam = DETAILS_FILTER_PARAMS[config.detailsFilterType];
      qs.set(detailsParam, rowId);
      qs.set('stackBy', stackBy);
      if (config.detailsFilterType === 'actors') {
        qs.set('actorRole', actorRole);
      }

      if (stackBy === 'coverage') {
        const coverage = COVERAGE_BY_SERIES[seriesName as string];
        if (!coverage) return;
        qs.set('coverage', String(coverage));
      } else {
        qs.set('category', seriesName as string);
      }

      if (jurisdictionFilter.length) qs.set('jurisdiction', jurisdictionFilter.join(','));
      if (authorityFilter.length) qs.set('authority', authorityFilter.join(','));
      if (legislativeStatusFilter.length) qs.set('legislativeStatus', legislativeStatusFilter.join(','));
      if (activityFilter.length) qs.set('activity', activityFilter.join(','));
      if (coverageFilter.length) qs.set('coverageFilter', coverageFilter.join(','));
      if (appliesToFilter.length) qs.set('appliesTo', appliesToFilter.join(','));
      if (lifecycleStageFilter.length) qs.set('lifecycleStage', lifecycleStageFilter.join(','));
      if (technicalScopeFilter.length) qs.set('technicalScope', technicalScopeFilter.join(','));
      if (riskSubdomainFilter.length) qs.set('riskSubdomain', riskSubdomainFilter.join(','));
      if (sectorFilter.length) qs.set('sector', sectorFilter.join(','));

      // Build active filter chips for display
      const COVERAGE_VALUE_LABELS: Record<string, string> = { '3': 'Good', '2': 'Minimal', '1': 'No coverage' };
      const STACK_BY_LABEL: Record<string, string> = {
        coverage: 'Coverage',
        jurisdiction: 'Jurisdiction',
        authority: 'Authority',
        legislativeStatus: 'Legislative Status',
        activity: 'Activity',
      };
      const filters: ActiveFilter[] = [];
      filters.push({ label: STACK_BY_LABEL[stackBy] || stackBy, values: [seriesName as string] });
      if (stackBy !== 'coverage' && coverageFilter.length) filters.push({ label: 'Coverage', values: coverageFilter.map(v => COVERAGE_VALUE_LABELS[v] || v) });
      if (stackBy !== 'jurisdiction' && jurisdictionFilter.length) filters.push({ label: 'Jurisdiction', values: [...jurisdictionFilter] });
      if (stackBy !== 'authority' && authorityFilter.length) filters.push({ label: 'Authority', values: [...authorityFilter] });
      if (stackBy !== 'legislativeStatus' && legislativeStatusFilter.length) filters.push({ label: 'Legislative Status', values: [...legislativeStatusFilter] });
      if (stackBy !== 'activity' && activityFilter.length) filters.push({ label: 'Activity', values: [...activityFilter] });
      if (appliesToFilter.length) filters.push({ label: 'Applies To', values: [...appliesToFilter] });
      if (lifecycleStageFilter.length) filters.push({ label: 'Lifecycle Stage', values: [...lifecycleStageFilter] });
      if (technicalScopeFilter.length) filters.push({ label: 'Technical Scope', values: [...technicalScopeFilter] });
      if (riskSubdomainFilter.length) filters.push({ label: 'Risk Subdomain', values: riskSubdomainFilter.map(id => getSubdomainName(id) ?? id) });
      if (sectorFilter.length) filters.push({ label: 'Sector', values: sectorFilter.map(id => SECTOR_FIELDS.find(sf => sf.id === id)?.label ?? id) });

      // Title: for risk subdomains use "id - label", for others just the label
      const modalTitle = config.tooltipHeaderFormat === 'id-label'
        ? `${rowId} - ${rowLabel}`
        : rowLabel;

      setListModalTitle(modalTitle);
      setListModalFilters(filters);
      setListModalOpen(true);
      setListModalLoading(true);
      setListModalItems([]);

      fetch(`${apiBase}/data/risk-subdomain/details?${qs}`)
        .then((r) => r.json())
        .then((json: { items?: DocumentListItem[] }) => {
          setListModalItems(json?.items ?? []);
        })
        .catch((err) => {
          console.error('Failed to fetch document list:', err);
        })
        .finally(() => {
          setListModalLoading(false);
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, config, stackBy, jurisdictionFilter.join(','), authorityFilter.join(','), legislativeStatusFilter.join(','), activityFilter.join(','), coverageFilter.join(','), appliesToFilter.join(','), lifecycleStageFilter.join(','), technicalScopeFilter.join(','), riskSubdomainFilter.join(','), sectorFilter.join(','), actorRole, data]);

  // Handle document click in list modal
  const handleDocumentClick = useCallback((documentId: string) => {
    setDetailDocumentId(documentId);
    setDetailModalOpen(true);
    setDetailModalLoading(true);
    setDetailDocument(null);

    fetch(`${apiBase}/data/risk-subdomain/document?documentId=${encodeURIComponent(documentId)}`)
      .then((r) => r.json())
      .then((json: { document?: DocumentDetail }) => {
        setDetailDocument(json?.document ?? null);
      })
      .catch((err) => {
        console.error('Failed to fetch document details:', err);
      })
      .finally(() => {
        setDetailModalLoading(false);
      });
  }, [apiBase]);

  // Resize chart on window resize
  useEffect(() => {
    const handleResize = () => {
      chartInstance.current?.resize();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      chartInstance.current?.dispose();
    };
  }, []);

  const wrapperTheme = getChartTheme(isDarkMode);

  return (
    <div style={{ position: 'relative', background: 'transparent', color: 'inherit' }}>
      <FilterControls
        stackBy={stackBy}
        onStackByChange={setStackBy}
        coverageFilter={coverageFilter}
        onCoverageFilterChange={setCoverageFilter}
        jurisdictionFilter={jurisdictionFilter}
        onJurisdictionFilterChange={setJurisdictionFilter}
        authorityFilter={authorityFilter}
        onAuthorityFilterChange={setAuthorityFilter}
        legislativeStatusFilter={legislativeStatusFilter}
        onLegislativeStatusFilterChange={setLegislativeStatusFilter}
        activityFilter={activityFilter}
        onActivityFilterChange={setActivityFilter}
        jurisdictions={jurisdictions}
        authorities={authorities}
        legislativeStatuses={legislativeStatuses}
        activities={activities}
        appliesToFilter={appliesToFilter}
        onAppliesToFilterChange={setAppliesToFilter}
        lifecycleStageFilter={lifecycleStageFilter}
        onLifecycleStageFilterChange={setLifecycleStageFilter}
        technicalScopeFilter={technicalScopeFilter}
        onTechnicalScopeFilterChange={setTechnicalScopeFilter}
        riskSubdomainFilter={riskSubdomainFilter}
        onRiskSubdomainFilterChange={setRiskSubdomainFilter}
        riskSubdomainOptions={RISK_SUBDOMAIN_OPTIONS}
        sectorFilter={sectorFilter}
        onSectorFilterChange={setSectorFilter}
        sectorOptions={SECTOR_OPTIONS}
        appliesToOptions={appliesToOptions}
        lifecycleStageOptions={lifecycleStageOptions}
        technicalScopeOptions={technicalScopeOptions}
        isMobile={isMobile}
        documentCount={data?.totalDocumentsAll}
        chartTitle={config.title}
        stackByOptions={config.stackByOptions}
        showCoverageFilter={config.showCoverageFilter}
        hiddenFilters={config.hiddenFilters}
        actorRole={config.aggregation === 'actors' ? actorRole : undefined}
        onActorRoleChange={config.aggregation === 'actors' ? (role: string) => setActorRole(role as ActorRole) : undefined}
        actorRoleOptions={config.aggregation === 'actors' ? ACTOR_ROLES : undefined}
        onReset={() => {
          setStackBy(config.defaultStackBy);
          setCoverageFilter([]);
          setJurisdictionFilter([]);
          setAuthorityFilter([]);
          setLegislativeStatusFilter([]);
          setActivityFilter([]);
          setAppliesToFilter([]);
          setLifecycleStageFilter([]);
          setTechnicalScopeFilter([]);
          setRiskSubdomainFilter([]);
          setSectorFilter([]);
          setActorRole(DEFAULT_ACTOR_ROLE);
        }}
      />

      <div style={{ position: 'relative' }}>
        <div
          ref={chartRef}
          onContextMenu={(e) => e.preventDefault()}
          style={{
            width: '100%',
            minHeight: 300,
          }}
        />

        <div
          ref={tooltipRef}
          style={{
            position: 'fixed',
            padding: '8px 12px',
            background: 'rgba(50, 50, 50, 0.95)',
            color: '#fff',
            borderRadius: '4px',
            fontSize: '12px',
            lineHeight: '1.5',
            maxWidth: '350px',
            pointerEvents: 'none',
            zIndex: 9999,
            display: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            wordWrap: 'break-word',
            textAlign: 'left',
          }}
        />

        {loading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(128,128,128,0.15)',
            }}
          >
            Loading...
          </div>
        )}

      </div>

      {/* X-axis label row: centered label + right-aligned view toggle */}
      {data && (
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'center' : 'baseline',
          justifyContent: isMobile ? 'center' : 'flex-start',
          position: 'relative',
          marginTop: 0,
          padding: '0 4px',
          gap: isMobile ? 2 : 0,
          minHeight: 20,
        }}>
          {isMobile ? (
            <>
              <div style={{ fontSize: 12, color: 'var(--gc-foreground, inherit)', textAlign: 'center' }}>
                {viewMode === 'proportion' ? 'Proportion of documents' : 'Document counts'}
              </div>
              <ViewModeToggle
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                isMobile={isMobile}
              />
            </>
          ) : (
            <>
              <div style={{ position: 'absolute', left: 0, right: 0, textAlign: 'center', fontSize: 12, color: 'var(--gc-foreground, inherit)', pointerEvents: 'none' }}>
                {viewMode === 'proportion' ? 'Proportion of documents' : 'Document counts'}
              </div>
              <div style={{ marginLeft: 'auto' }}>
                <ViewModeToggle
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  isMobile={isMobile}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* Custom footer: legend */}
      {data && (
        <div style={{
          gap: 8,
          marginTop: 4,
          padding: '0 4px',
        }}>
          <div>
            <div style={{ fontSize: 12, color: 'var(--gc-text-muted, #6b7280)', marginBottom: 4 }}>
              {STACK_BY_LABELS[stackBy]}{' '}
              <span style={{ fontStyle: 'italic', fontSize: 10 }}>(click categories to toggle visibility)</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {data.seriesCategories.map((cat, i) => {
                const isHidden = hiddenSeries.has(cat);
                const color = stackBy === 'coverage' ? getCoverageColor(cat) : getCategoryColor(i);
                return (
                  <div
                    key={cat}
                    onClick={() => {
                      const next = new Set(hiddenSeries);
                      if (next.has(cat)) {
                        next.delete(cat);
                      } else {
                        next.add(cat);
                      }
                      setHiddenSeries(next);
                      if (chartInstance.current) {
                        chartInstance.current.dispatchAction({
                          type: 'legendToggleSelect',
                          name: cat,
                        });
                      }
                    }}
                    onMouseEnter={(e) => {
                      if (stackBy === 'coverage' && COVERAGE_LEGEND_TOOLTIPS[cat] && tooltipRef.current) {
                        const desc = COVERAGE_LEGEND_TOOLTIPS[cat];
                        const colonIdx = desc.indexOf(':');
                        const description = colonIdx !== -1 ? desc.slice(colonIdx + 1).trim() : '';
                        tooltipRef.current.innerHTML = `<strong>${cat}</strong>${description ? '<br/>' + description : ''}`;
                        tooltipRef.current.style.display = 'block';
                        const mouseEvent = e as unknown as MouseEvent;
                        const el = tooltipRef.current;
                        const tooltipWidth = el.offsetWidth;
                        const tooltipHeight = el.offsetHeight;
                        const vw = window.innerWidth;
                        const vh = window.innerHeight;
                        let left = mouseEvent.clientX + 15;
                        let top = mouseEvent.clientY - 10;
                        if (left + tooltipWidth > vw - 10) left = mouseEvent.clientX - tooltipWidth - 15;
                        if (top + tooltipHeight > vh - 10) top = vh - tooltipHeight - 10;
                        if (top < 10) top = 10;
                        if (left < 10) left = 10;
                        el.style.left = `${left}px`;
                        el.style.top = `${top}px`;
                      }
                    }}
                    onMouseLeave={() => {
                      if (tooltipRef.current) {
                        tooltipRef.current.style.display = 'none';
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                      cursor: 'pointer',
                      opacity: isHidden ? 0.35 : 1,
                      fontSize: 12,
                      color: 'var(--gc-foreground, inherit)',
                      userSelect: 'none',
                    }}
                  >
                    <span style={{
                      display: 'inline-block',
                      width: 14,
                      height: 14,
                      borderRadius: 2,
                      background: color,
                      flexShrink: 0,
                    }} />
                    {cat}
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      )}

      <DocumentListModal
        isOpen={listModalOpen}
        onClose={() => setListModalOpen(false)}
        title={listModalTitle}
        items={listModalItems}
        loading={listModalLoading}
        onDocumentClick={handleDocumentClick}
        activeFilters={listModalFilters}
      />

      <DocumentDetailModal
        isOpen={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        documentId={detailDocumentId}
        document={detailDocument}
        loading={detailModalLoading}
      />
    </div>
  );
}
